import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Power, Save, AlertTriangle, X, Loader2, DollarSign, Activity, ShieldAlert, ShieldCheck, Bug, Users, BookOpen, Route, Layers, Sparkles, GraduationCap, Mail, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import SupervisionPanel from '../components/admin/SupervisionPanel';
import CatalogAdminPanel from '../components/admin/CatalogAdminPanel';
import KnowledgeAdminPanel from '../components/admin/KnowledgeAdminPanel';
import RequestTracesPanel from '../components/admin/RequestTracesPanel';
import KbSourcesPanel from '../components/admin/KbSourcesPanel';
import LearningPanel from '../components/admin/LearningPanel';
import EmailTemplatePanel from '../components/admin/EmailTemplatePanel';
import EmailAllowlistPanel from '../components/admin/EmailAllowlistPanel';
import GuardrailsPanel from '../components/admin/GuardrailsPanel';
import UsersPanel from '../components/admin/UsersPanel';
import TopUsersTable from '../components/admin/TopUsersTable';
import {
  ADMIN_EMAIL, getCostSummary, getTopUsers, setBudgetConfig, pauseLLM, getAgentPopularity,
  getRiskyUsers, setUserDisabled, getUserUsageBreakdown, getErrorGroups, analyzeErrors, getAiSpend, getTelemetryAnalytics,
  type CostSummary, type BudgetConfig, type RiskyUser, type UserUsageBreakdown, type ErrorGroupRow, type TopUserRow, type AiSpendBreakdown, type TelemetryBreakdown,
} from '../services/adminService';

const usd = (n: number) => `$${(n || 0).toFixed(2)}`;

// Side-tab agendas: switch admin focus between cost, supervision, moderation, and error repair.
const ADMIN_TABS = [
  { key: 'cost', label: 'Cost', Icon: DollarSign },
  { key: 'engagement', label: 'Engagement', Icon: TrendingUp },
  { key: 'supervision', label: 'Supervision', Icon: Activity },
  { key: 'moderation', label: 'Moderation', Icon: ShieldAlert },
  { key: 'safety', label: 'Safety', Icon: ShieldCheck },
  { key: 'errors', label: 'Errors', Icon: Bug },
  { key: 'traces', label: 'Traces', Icon: Route },
  { key: 'users', label: 'Users', Icon: Users },
  { key: 'agents', label: 'Agents', Icon: Users },
  { key: 'knowledge', label: 'Knowledge', Icon: BookOpen },
  { key: 'kbsources', label: 'KB Sources', Icon: Layers },
  { key: 'learning', label: 'Learning', Icon: GraduationCap },
  { key: 'email', label: 'Email', Icon: Mail },
] as const;
type AdminTab = typeof ADMIN_TABS[number]['key'];

/** Admin-only cost management: spend, budget controls, kill switch, per-user caps. */
const AdminCostPage: React.FC = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [cfg, setCfg] = useState<BudgetConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Per-call ai_spend breakdown (by tier + day over a window) — the granular companion to the monthly summary.
  const [spend, setSpend] = useState<AiSpendBreakdown | null>(null);
  const [spendDays, setSpendDays] = useState<7 | 30 | 90>(30);
  useEffect(() => { getAiSpend(spendDays).then(setSpend).catch((e) => console.error('[admin] ai spend load failed:', e)); }, [spendDays]);
  const [promo, setPromo] = useState<{ code: string; reward: { wp: number }; redeemed: number; cap: number | null } | null>(null);
  useEffect(() => { import('../services/walletService').then(({ getActivePromoCode }) => getActivePromoCode().then(setPromo).catch(() => {})); }, []);
  const [agents, setAgents] = useState<import('../services/adminService').AgentPopularity[]>([]);
  const [agentRange, setAgentRange] = useState<0 | 7 | 30 | 90>(0);
  // Surface load failures (this is an observability page — silent catches hid why Supervision looked empty).
  useEffect(() => { getAgentPopularity(50, agentRange).then((r) => setAgents(r.agents || [])).catch((e) => { console.error('[admin] agent popularity load failed:', e); setError(e?.message || 'Failed to load agent activity'); }); }, [agentRange]);
  const [trending, setTrending] = useState<import('../services/mediaShareService').TrendingItem[]>([]);
  useEffect(() => { import('../services/mediaShareService').then(({ getTrendingMedia }) => getTrendingMedia().then(setTrending).catch((e) => { console.error('[admin] trending media load failed:', e); })); }, []);
  const [risky, setRisky] = useState<RiskyUser[]>([]);
  const loadRisky = useCallback(() => { getRiskyUsers(50).then(setRisky).catch(() => {}); }, []);
  useEffect(() => { loadRisky(); }, [loadRisky]);
  const [confirmUser, setConfirmUser] = useState<RiskyUser | null>(null);
  const [breakdownUid, setBreakdownUid] = useState<string | null>(null);
  const [tab, setTab] = useState<AdminTab>('cost');
  // Product-usage engagement (telemetry_events spine) — loaded only when its tab is open.
  const [engagement, setEngagement] = useState<TelemetryBreakdown | null>(null);
  const [engDays, setEngDays] = useState<7 | 30>(7);
  useEffect(() => { if (tab === 'engagement') getTelemetryAnalytics(engDays).then(setEngagement).catch((e) => console.error('[admin] engagement load failed:', e)); }, [tab, engDays]);
  const runToggleDisabled = async (u: RiskyUser) => {
    try { await setUserDisabled(u.uid, !u.disabled); loadRisky(); } catch (e: any) { setError(e?.message || 'Failed'); }
  };

  // Top users by ROLLING window (7/30/90 days) — more useful than a calendar-month total for spotting
  // who is heavy right now. From the supervision log (chat + media), which has daily granularity.
  const [topDays, setTopDays] = useState<7 | 30 | 90>(30);
  const [topUsers, setTopUsers] = useState<TopUserRow[]>([]);
  const loadTop = useCallback(() => {
    getTopUsers(topDays).then((r) => setTopUsers(r.users || [])).catch((e) => console.error('[admin] top users load failed:', e));
  }, [topDays]);
  useEffect(() => { loadTop(); }, [loadTop]);

  const load = useCallback(() => {
    getCostSummary()
      .then((s) => { setSummary(s); setCfg(s.config); })
      .catch((e) => setError(e?.message || 'Failed to load'));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.email !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;

  const saveConfig = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      await setBudgetConfig({
        monthly_budget_usd: Number(cfg.monthly_budget_usd),
        per_user_monthly_usd: Number(cfg.per_user_monthly_usd),
        hard_stop: cfg.hard_stop,
      });
      load();
    } catch (e: any) { setError(e?.message || 'Save failed'); } finally { setBusy(false); }
  };

  const togglePause = async () => {
    if (!cfg) return;
    setBusy(true);
    try { await pauseLLM(!cfg.paused); load(); } finally { setBusy(false); }
  };

  const pct = summary ? Math.min(1, summary.budgetUsedPct) : 0;
  const over = summary ? summary.budgetUsedPct >= 1 : false;

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <header className="border-b border-secondary-200 dark:border-secondary-800">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center gap-4">
          {/* Breadcrumb — Dashboard / Admin console / <active section>; each level navigates up one. */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm min-w-0">
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-secondary-500 hover:text-secondary-900 dark:hover:text-white shrink-0">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-secondary-300 dark:text-secondary-600 shrink-0" />
            <button onClick={() => setTab('cost')}
              className={tab === 'cost' ? 'font-bold text-secondary-900 dark:text-white shrink-0' : 'text-secondary-500 hover:text-secondary-900 dark:hover:text-white shrink-0'}>
              Admin console
            </button>
            {tab !== 'cost' && (
              <>
                <ChevronRight className="w-3.5 h-3.5 text-secondary-300 dark:text-secondary-600 shrink-0" />
                <span className="font-semibold truncate">{ADMIN_TABS.find((t) => t.key === tab)?.label}</span>
              </>
            )}
          </nav>
          {summary && <span className="ml-auto text-sm text-secondary-400 shrink-0">{summary.month}</span>}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 flex gap-6">
        {/* Side tabs — switch admin focus (cost · supervision · moderation · errors) */}
        <nav className="shrink-0 flex flex-col gap-1 w-11 sm:w-44">
          {ADMIN_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} title={t.label}
              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium ${tab === t.key ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
              <t.Icon className="w-4 h-4 shrink-0" /><span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <main className="flex-1 min-w-0 space-y-8">
          {error && <p className="text-sm text-red-500">{error}</p>}

          {tab === 'cost' && (
          <>
        {/* This month's promo code — share across channels to drive traffic (admin-only). */}
        {promo && (
          <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
            <p className="text-sm text-secondary-500">This month's promo code</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <code className="text-lg font-bold tracking-widest rounded-lg bg-secondary-100 dark:bg-secondary-800 px-3 py-1.5">{promo.code}</code>
              <button onClick={() => navigator.clipboard?.writeText(promo.code)} className="text-xs text-secondary-500 hover:text-secondary-900 dark:hover:text-white">Copy</button>
              <span className="text-sm text-secondary-500">grants +{promo.reward.wp.toLocaleString()} WP</span>
            </div>
            <p className="mt-2 text-xs text-secondary-400">
              Redeemed {promo.redeemed.toLocaleString()}{promo.cap ? ` / ${promo.cap.toLocaleString()}` : ''} · once per user · rotates next month
            </p>
          </div>
        )}
        {!summary || !cfg ? (
          <p className="text-secondary-400">Loading…</p>
        ) : (
          <>
            {/* Kill switch */}
            <div className={`flex items-center justify-between rounded-2xl border p-5 ${cfg.paused ? 'border-red-400 bg-red-50 dark:bg-red-900/10' : 'border-secondary-200 dark:border-secondary-800'}`}>
              <div>
                <p className="font-semibold flex items-center gap-2">
                  <Power className={`w-4 h-4 ${cfg.paused ? 'text-red-500' : 'text-secondary-400'}`} />
                  {cfg.paused ? 'AI is PAUSED for everyone' : 'AI is active'}
                </p>
                <p className="text-sm text-secondary-500">Emergency stop — blocks all model calls instantly.</p>
              </div>
              <button onClick={togglePause} disabled={busy}
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${cfg.paused ? 'bg-secondary-900 dark:bg-white text-white dark:text-secondary-900' : 'bg-red-500 text-white hover:bg-red-600'}`}>
                {cfg.paused ? 'Resume' : 'Pause all'}
              </button>
            </div>

            {/* Spend this month */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="text-sm text-secondary-500">Spent this month (estimated)</p>
                  <p className="text-3xl font-bold">{usd(summary.totals.cost_usd)} <span className="text-base font-normal text-secondary-400">/ {usd(cfg.monthly_budget_usd)}</span></p>
                </div>
                <div className="text-right text-sm text-secondary-500">
                  <p>{summary.totals.requests} requests</p>
                  <p>{summary.totals.tokens.toLocaleString()} tokens</p>
                </div>
              </div>
              <div className="h-2 w-full rounded-full bg-secondary-200 dark:bg-secondary-800 overflow-hidden">
                <div className={`h-full rounded-full ${over ? 'bg-red-500' : 'bg-secondary-900 dark:bg-white'}`} style={{ width: `${pct * 100}%` }} />
              </div>
              {over && <p className="mt-2 text-sm text-red-500 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Budget reached — {cfg.hard_stop ? 'new requests are blocked.' : 'alert-only (not blocking).'}</p>}

              {/* Spend breakdown by category (text vs media types) */}
              {(() => {
                const by = summary.totals.byCategory || {};
                const order = ['text', 'proactive', 'image', 'video', 'music', 'voice', 'files'];
                const rows = order.filter((k) => (by[k] || 0) > 0).map((k) => ({ k, v: by[k] }));
                if (rows.length === 0) return null;
                const max = Math.max(...rows.map((r) => r.v));
                const labels: Record<string, string> = { text: 'Text (LLM)', proactive: 'Free agent chatter', image: 'Images', video: 'Video', music: 'Music & SFX', voice: 'Voice', files: 'Files' };
                return (
                  <div className="mt-5 pt-4 border-t border-secondary-100 dark:border-secondary-800">
                    <p className="text-xs uppercase tracking-wide text-secondary-400 mb-2">Breakdown</p>
                    <div className="space-y-1.5">
                      {rows.map((r) => (
                        <div key={r.k} className="flex items-center gap-3">
                          <span className="w-24 shrink-0 text-xs text-secondary-500">{labels[r.k] || r.k}</span>
                          <div className="flex-1 h-2 rounded-full bg-secondary-100 dark:bg-secondary-800 overflow-hidden">
                            <div className="h-full rounded-full bg-secondary-900 dark:bg-white" style={{ width: `${max > 0 ? (r.v / max) * 100 : 0}%` }} />
                          </div>
                          <span className="w-16 shrink-0 text-right text-xs font-medium text-secondary-700 dark:text-secondary-200 tabular-nums">{usd(r.v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* AI cost breakdown from the per-call ai_spend ledger (by tier + day, windowed) */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <h2 className="font-semibold">AI cost — last {spendDays} days</h2>
                  <p className="text-xs text-secondary-400">per-call ledger · by tier &amp; day</p>
                </div>
                <div className="flex gap-1">
                  {([7, 30, 90] as const).map((d) => (
                    <button key={d} type="button" onClick={() => setSpendDays(d)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium ${spendDays === d ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>{d}d</button>
                  ))}
                </div>
              </div>
              {!spend ? (
                <p className="text-sm text-secondary-400">Loading…</p>
              ) : spend.totals.requests === 0 ? (
                <p className="text-sm text-secondary-400">No AI calls recorded in this window yet.</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div><p className="text-xs text-secondary-400">Cost</p><p className="text-lg font-semibold tabular-nums">{usd(spend.totals.costUsd)}</p></div>
                    <div><p className="text-xs text-secondary-400">Calls</p><p className="text-lg font-semibold tabular-nums">{spend.totals.requests.toLocaleString()}</p></div>
                    <div><p className="text-xs text-secondary-400">Tokens</p><p className="text-lg font-semibold tabular-nums">{spend.totals.tokens.toLocaleString()}</p></div>
                  </div>
                  {(() => {
                    const rows = Object.entries(spend.byTier).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
                    if (!rows.length) return null;
                    const max = Math.max(...rows.map(([, v]) => v));
                    return (
                      <div className="mb-4">
                        <p className="text-xs uppercase tracking-wide text-secondary-400 mb-2">By model tier</p>
                        <div className="space-y-1.5">
                          {rows.map(([tier, v]) => (
                            <div key={tier} className="flex items-center gap-3">
                              <span className="w-20 shrink-0 text-xs text-secondary-500">{tier}</span>
                              <div className="flex-1 h-2 rounded-full bg-secondary-100 dark:bg-secondary-800 overflow-hidden"><div className="h-full rounded-full bg-secondary-900 dark:bg-white" style={{ width: `${max > 0 ? (v / max) * 100 : 0}%` }} /></div>
                              <span className="w-16 shrink-0 text-right text-xs font-medium tabular-nums">{usd(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {spend.byDay.length > 0 && (() => {
                    const max = Math.max(...spend.byDay.map((d) => d.costUsd));
                    return (
                      <div>
                        <p className="text-xs uppercase tracking-wide text-secondary-400 mb-2">Daily</p>
                        <div className="flex items-end gap-0.5 h-16">
                          {spend.byDay.map((d) => (
                            <div key={d.day} className="flex-1 min-w-0 flex flex-col justify-end" title={`${d.day}: ${usd(d.costUsd)} · ${d.requests} calls`}>
                              <div className="rounded-t bg-secondary-900 dark:bg-white" style={{ height: `${max > 0 ? Math.max(2, (d.costUsd / max) * 100) : 2}%` }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {spend.capped && <p className="mt-3 text-xs text-amber-500">Showing the most recent {spend.scanned.toLocaleString()} calls (window exceeded the read cap — totals are a floor).</p>}
                </>
              )}
            </div>

            {/* Budget controls */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5 space-y-4">
              <h2 className="font-semibold">Budget controls</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="text-sm">Global monthly budget ($)
                  <input type="number" min={0} value={cfg.monthly_budget_usd}
                    onChange={(e) => setCfg({ ...cfg, monthly_budget_usd: Number(e.target.value) })}
                    className="mt-1 w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2" />
                </label>
                <label className="text-sm">Default per-user monthly cap ($)
                  <input type="number" min={0} value={cfg.per_user_monthly_usd}
                    onChange={(e) => setCfg({ ...cfg, per_user_monthly_usd: Number(e.target.value) })}
                    className="mt-1 w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={cfg.hard_stop} onChange={(e) => setCfg({ ...cfg, hard_stop: e.target.checked })} />
                Hard-stop at the budget (off = alert-only, keeps serving)
              </label>
              <button onClick={saveConfig} disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 text-sm font-semibold disabled:opacity-50">
                <Save className="w-4 h-4" /> Save controls
              </button>
            </div>

            {/* Top users — rolling window (7/30/90 days), ranked by recent AI cost */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="font-semibold">Top users · last {topDays} days</h2>
                <div className="flex gap-1">
                  {([7, 30, 90] as const).map((v) => (
                    <button key={v} onClick={() => setTopDays(v)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium ${topDays === v ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
                      {v}d
                    </button>
                  ))}
                </div>
              </div>
              {topUsers.length === 0 ? (
                <p className="text-sm text-secondary-400">No usage in the last {topDays} days.</p>
              ) : (
                <TopUsersTable users={topUsers} overrides={cfg.per_user_overrides || {}}
                  onChange={() => { load(); loadTop(); }} setBusy={setBusy} onOpen={(uid) => setBreakdownUid(uid)} />
              )}
              <p className="mt-2 text-[11px] text-secondary-400">Ranked by estimated AI cost across chat + media over the window.</p>
            </div>
              </>
            )}
          </>
          )}

          {tab === 'engagement' && (
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <h2 className="font-semibold">Product usage — last {engDays} days</h2>
                  <p className="text-xs text-secondary-400">in-app telemetry spine · PII-free</p>
                </div>
                <div className="flex gap-1">
                  {([7, 30] as const).map((d) => (
                    <button key={d} type="button" onClick={() => setEngDays(d)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium ${engDays === d ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>{d}d</button>
                  ))}
                </div>
              </div>
              {!engagement ? (
                <p className="text-sm text-secondary-400">Loading…</p>
              ) : engagement.totals.events === 0 ? (
                <p className="text-sm text-secondary-400">No product-usage events recorded in this window yet.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div><p className="text-xs text-secondary-400">Events</p><p className="text-lg font-semibold tabular-nums">{engagement.totals.events.toLocaleString()}</p></div>
                    <div><p className="text-xs text-secondary-400">Distinct users</p><p className="text-lg font-semibold tabular-nums">{engagement.totals.users.toLocaleString()}</p></div>
                  </div>
                  {engagement.byDay.length > 0 && (() => {
                    const max = Math.max(...engagement.byDay.map((d) => d.count));
                    return (
                      <div className="mb-4">
                        <p className="text-xs uppercase tracking-wide text-secondary-400 mb-2">Daily events</p>
                        <div className="flex items-end gap-0.5 h-16">
                          {engagement.byDay.map((d) => (
                            <div key={d.day} className="flex-1 min-w-0 flex flex-col justify-end" title={`${d.day}: ${d.count.toLocaleString()} events`}>
                              <div className="rounded-t bg-secondary-900 dark:bg-white" style={{ height: `${max > 0 ? Math.max(2, (d.count / max) * 100) : 2}%` }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    const rows = engagement.byEvent.slice(0, 12);
                    const max = Math.max(...rows.map((r) => r.count));
                    return (
                      <div>
                        <p className="text-xs uppercase tracking-wide text-secondary-400 mb-2">Top events</p>
                        <div className="space-y-1.5">
                          {rows.map((r) => (
                            <div key={r.event} className="flex items-center gap-3">
                              <span className="w-40 shrink-0 text-xs text-secondary-500 truncate">{r.event}</span>
                              <div className="flex-1 h-2 rounded-full bg-secondary-100 dark:bg-secondary-800 overflow-hidden"><div className="h-full rounded-full bg-secondary-900 dark:bg-white" style={{ width: `${max > 0 ? (r.count / max) * 100 : 0}%` }} /></div>
                              <span className="w-14 shrink-0 text-right text-xs font-medium tabular-nums">{r.count.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {engagement.capped && <p className="mt-3 text-xs text-amber-500">Showing the most recent {engagement.scanned.toLocaleString()} events (window exceeded the read cap).</p>}
                </>
              )}
            </div>
          )}

          {tab === 'supervision' && (
          <>
            {/* Traffic analysis (7/30d) + masked prompt/response feed + per-user (by email) drill-down */}
            <SupervisionPanel />

            {/* Agent popularity — busiest agents platform-wide (admin-only), by time window */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="font-semibold">Most popular agents</h2>
                <div className="flex gap-1">
                  {([[0, 'All'], [7, '7d'], [30, '30d'], [90, '90d']] as const).map(([v, label]) => (
                    <button key={v} onClick={() => setAgentRange(v)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium ${agentRange === v ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {agents.length === 0 ? (
                <p className="text-sm text-secondary-400">No agent activity {agentRange ? `in the last ${agentRange} days` : 'yet'}.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
                    <tr><th className="py-1">#</th><th>Agent</th><th>Replies{agentRange ? ` · ${agentRange}d` : ''}</th><th>Last active</th></tr>
                  </thead>
                  <tbody>
                    {agents.map((a, i) => (
                      <tr key={a.profileId} className="border-t border-secondary-100 dark:border-secondary-800">
                        <td className="py-1.5 text-secondary-400">{i + 1}</td>
                        <td className="font-medium text-secondary-900 dark:text-white">{a.name}</td>
                        <td>{a.replyCount.toLocaleString()}</td>
                        <td className="text-secondary-400 whitespace-nowrap" title={a.lastActive || ''}>
                          {a.lastActive ? new Date(a.lastActive).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Trending media — virality detection (admin-only); recent-view velocity */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <h2 className="font-semibold mb-3">Trending media (virality)</h2>
              {trending.length === 0 ? (
                <p className="text-sm text-secondary-400">No trending media yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
                    <tr><th className="py-1">#</th><th>Title</th><th>Kind</th><th>Views · recent</th><th>Total</th></tr>
                  </thead>
                  <tbody>
                    {trending.map((t, i) => (
                      <tr key={t.media_id} className="border-t border-secondary-100 dark:border-secondary-800">
                        <td className="py-1.5 text-secondary-400">{i + 1}</td>
                        <td className="font-medium text-secondary-900 dark:text-white truncate max-w-[200px]">
                          <a href={`/m/${t.media_id}`} target="_blank" rel="noreferrer" className="hover:underline">{t.title || 'Untitled'}</a>
                        </td>
                        <td className="text-secondary-400">{t.kind}</td>
                        <td>{t.recent_views.toLocaleString()}</td>
                        <td className="text-secondary-400">{t.views_total.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
          )}

          {tab === 'moderation' && (
          <>
            {/* Risky users — most content-policy trips, for manual moderation (admin-only) */}
            <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
              <h2 className="font-semibold mb-3">Risky users (content-policy trips)</h2>
              {risky.length === 0 ? (
                <p className="text-sm text-secondary-400">No flagged activity.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
                    <tr><th className="py-1">User</th><th>Trips</th><th>Last</th><th>Recent</th><th></th></tr>
                  </thead>
                  <tbody>
                    {risky.map((u) => (
                      <tr key={u.uid} className="border-t border-secondary-100 dark:border-secondary-800 align-top">
                        <td className="py-1.5 font-medium text-secondary-900 dark:text-white break-all">
                          {u.email || u.uid}{u.disabled && <span className="ml-1.5 text-xs text-red-500">(disabled)</span>}
                        </td>
                        <td className="text-red-500 font-semibold">{u.riskCount}</td>
                        <td className="text-secondary-400">{u.lastRisk || '—'}</td>
                        <td className="text-secondary-400 text-xs max-w-xs">{u.recent.slice(0, 2).map((r) => r.excerpt).join(' · ') || '—'}</td>
                        <td>
                          <button onClick={() => setConfirmUser(u)}
                            className={`text-xs px-2 py-1 rounded-md ${u.disabled ? 'text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800' : 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950'}`}>
                            {u.disabled ? 'Re-enable' : 'Disable'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
          )}

          {tab === 'safety' && <GuardrailsPanel />}
          {tab === 'errors' && <ErrorsPanel />}
          {tab === 'traces' && <RequestTracesPanel />}
          {tab === 'agents' && <CatalogAdminPanel />}
          {tab === 'knowledge' && <KnowledgeAdminPanel />}
          {tab === 'kbsources' && <KbSourcesPanel />}
          {tab === 'learning' && <LearningPanel />}
          {tab === 'users' && <UsersPanel />}
          {tab === 'email' && <div className="space-y-5"><EmailAllowlistPanel /><EmailTemplatePanel /></div>}
        </main>
      </div>
      <ConfirmDialog
        open={!!confirmUser}
        onOpenChange={(o) => { if (!o) setConfirmUser(null); }}
        title={confirmUser?.disabled ? 'Re-enable this account?' : 'Disable this account?'}
        description={confirmUser ? `${confirmUser.disabled ? 'Re-enable' : 'Disable'} ${confirmUser.email || confirmUser.uid}?` : ''}
        confirmText={confirmUser?.disabled ? 'Re-enable' : 'Disable'}
        variant={confirmUser?.disabled ? 'default' : 'destructive'}
        onConfirm={() => { if (confirmUser) runToggleDisabled(confirmUser); setConfirmUser(null); }}
      />
      {breakdownUid && <UserBreakdownModal uid={breakdownUid} onClose={() => setBreakdownUid(null)} />}
    </div>
  );
};

/** Per-user usage breakdown modal (admin) — AI spend, wallet, creations, conversations. PII masked. */
const UserBreakdownModal: React.FC<{ uid: string; onClose: () => void }> = ({ uid, onClose }) => {
  const [data, setData] = useState<UserUsageBreakdown | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    getUserUsageBreakdown(uid).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setErr(e?.message || 'Failed to load'); });
    return () => { alive = false; };
  }, [uid]);
  const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="rounded-lg bg-secondary-50 dark:bg-secondary-800/50 px-3 py-2">
      <p className="text-lg font-semibold text-secondary-900 dark:text-white">{value}</p>
      <p className="text-[11px] text-secondary-400">{label}</p>
    </div>
  );
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-secondary-200 dark:border-secondary-800">
          <div>
            <h2 className="text-sm font-semibold text-secondary-900 dark:text-white">{data?.email || 'User'} <span className="text-secondary-400 font-normal">· usage</span></h2>
            <p className="text-[11px] text-secondary-400">PII masked · {data?.month}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">
          {err ? <p className="text-sm text-red-500">{err}</p>
            : !data ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>
            : (
              <div className="space-y-4">
                <p className="text-xs text-secondary-500">Plan: <span className="font-medium text-secondary-800 dark:text-secondary-200">{data.plan}</span></p>
                <div>
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-secondary-400">AI usage this month</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Stat label="Requests" value={data.spend.requests.toLocaleString()} />
                    <Stat label="Tokens" value={data.spend.tokens.toLocaleString()} />
                    <Stat label="Est. cost" value={`$${data.spend.costUsd.toFixed(2)}`} />
                  </div>
                </div>
                <div>
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-secondary-400">Wallet</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Stat label="WP balance" value={data.wallet.wp.toLocaleString()} />
                    <Stat label="WP spent" value={data.wallet.wpSpent.toLocaleString()} />
                  </div>
                </div>
                <div>
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-secondary-400">Created</p>
                  <div className="grid grid-cols-4 gap-2">
                    <Stat label="Images" value={data.creations.image} />
                    <Stat label="Videos" value={data.creations.video} />
                    <Stat label="Audio" value={data.creations.audio} />
                    <Stat label="Chats" value={data.conversations ?? '—'} />
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};


/**
 * Errors panel (admin) — live, grouped platform errors (frontend + backend) collected automatically,
 * no user reports needed. Grouped by signature; filter by time window + source; ranked by frequency.
 */
const ErrorsPanel: React.FC = () => {
  const [days, setDays] = useState<1 | 7 | 30>(7);
  const [source, setSource] = useState<'' | 'frontend' | 'backend'>('');
  const [version, setVersion] = useState('');
  const [versions, setVersions] = useState<string[]>([]);
  const [rows, setRows] = useState<ErrorGroupRow[] | null>(null);
  const [scanned, setScanned] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  // AI triage side pane: a prioritized, copy-paste-ready fix list for the current error window.
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeErr, setAnalyzeErr] = useState('');
  const [copied, setCopied] = useState(false);
  const runAnalyze = () => {
    setAnalyzing(true); setAnalyzeErr(''); setAnalysis(null); setCopied(false);
    analyzeErrors(days, source || undefined, version || undefined)
      .then((r) => setAnalysis(r.analysis))
      .catch((e) => setAnalyzeErr(e?.message || 'Analysis failed'))
      .finally(() => setAnalyzing(false));
  };
  const copyAnalysis = () => { if (analysis) { navigator.clipboard?.writeText(analysis).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => { /* ignore */ }); } };
  useEffect(() => {
    let alive = true; setRows(null);
    getErrorGroups(days, source || undefined, version || undefined).then((r) => {
      if (!alive) return;
      setRows(r.groups); setScanned(r.scanned);
      // Populate the version dropdown from the full window (the backend returns all versions regardless of filter).
      if (r.versions?.length) setVersions(r.versions);
    }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [days, source, version]);
  const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
    <button onClick={onClick} className={`rounded-md px-2.5 py-1 text-xs font-medium ${active ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>{children}</button>
  );
  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">Errors <span className="text-secondary-400 font-normal text-sm">· auto-collected, grouped</span></h2>
        <div className="flex gap-2">
          <div className="flex gap-1">{([[1, '24h'], [7, '7d'], [30, '30d']] as const).map(([v, l]) => <Chip key={v} active={days === v} onClick={() => setDays(v)}>{l}</Chip>)}</div>
          <div className="flex gap-1">
            <Chip active={source === ''} onClick={() => setSource('')}>All</Chip>
            <Chip active={source === 'frontend'} onClick={() => setSource('frontend')}>Frontend</Chip>
            <Chip active={source === 'backend'} onClick={() => setSource('backend')}>Backend</Chip>
          </div>
          {versions.length > 1 && (
            <select value={version} onChange={(e) => setVersion(e.target.value)} title="Filter to a release version"
              className="rounded-md border border-secondary-200 dark:border-secondary-800 bg-transparent px-2 py-1 text-xs font-medium text-secondary-600 dark:text-secondary-300 focus:outline-none">
              <option value="">All versions</option>
              {versions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          <button onClick={runAnalyze} disabled={analyzing || !rows || rows.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1 text-xs font-semibold text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40">
            {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Analyze with AI
          </button>
        </div>
      </div>
      <div className="lg:flex lg:gap-4">
       <div className="min-w-0 flex-1">
      {rows === null ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-secondary-400">No errors in this window — all clear.</p>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="text-secondary-400 text-left text-xs uppercase tracking-wide">
              <tr><th className="py-1">Count</th><th>Source</th><th>Where</th><th>Error</th><th>Users</th><th>Version</th><th>Last seen</th></tr>
            </thead>
            <tbody>
              {rows.map((g) => {
                const open = expanded === g.signature;
                return (
                <React.Fragment key={g.signature}>
                <tr className="border-t border-secondary-100 dark:border-secondary-800 align-top cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-900/40"
                  onClick={() => setExpanded(open ? null : g.signature)} title="Click to see the full message, stack, and detail">
                  <td className="py-1.5 font-semibold text-secondary-900 dark:text-white">{g.count.toLocaleString()}</td>
                  <td><span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${g.source === 'frontend' ? 'bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300' : 'bg-secondary-900 dark:bg-secondary-100 text-white dark:text-secondary-900'}`}>{g.source}</span></td>
                  <td className="text-secondary-500 whitespace-nowrap max-w-[140px] truncate" title={g.route}>{g.route}{g.status ? ` · ${g.status}` : ''}</td>
                  <td className="text-secondary-500 text-xs max-w-[260px] truncate" title={g.sample}>{g.name ? <span className="font-medium text-secondary-700 dark:text-secondary-300">{g.name}: </span> : null}{g.sample}</td>
                  <td className="text-secondary-400">{g.users}</td>
                  <td className="text-secondary-400 whitespace-nowrap text-xs font-mono">{g.version || '—'}</td>
                  <td className="text-secondary-400 whitespace-nowrap text-xs">{g.lastSeen ? new Date(g.lastSeen).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                </tr>
                {open && (
                  <tr className="border-t border-secondary-100 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-900/40">
                    <td colSpan={7} className="px-3 py-3">
                      {/* Full error message (no longer truncated) — the actual thing that went wrong. */}
                      <div className="mb-2 rounded-lg bg-white dark:bg-black border border-secondary-200 dark:border-secondary-800 p-3 text-xs text-secondary-800 dark:text-secondary-100 whitespace-pre-wrap break-words">
                        {g.name ? <span className="font-semibold">{g.name}: </span> : null}{g.sample || '(no message captured)'}
                      </div>
                      <div className="text-[11px] text-secondary-500 mb-2 flex flex-wrap gap-x-3 gap-y-0.5">
                        {g.kind ? <span><span className="text-secondary-400">kind:</span> {g.kind}</span> : null}
                        {g.version ? <span><span className="text-secondary-400">version:</span> {g.version}</span> : null}
                        {g.code ? <span><span className="text-secondary-400">code:</span> {g.code}</span> : null}
                        <span><span className="text-secondary-400">route:</span> {g.route}{g.status ? ` · ${g.status}` : ''}</span>
                        <span><span className="text-secondary-400">count:</span> {g.count.toLocaleString()}</span>
                        <span><span className="text-secondary-400">users:</span> {g.users}</span>
                        {g.firstSeen ? <span><span className="text-secondary-400">first:</span> {new Date(g.firstSeen).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span> : null}
                        {g.lastSeen ? <span><span className="text-secondary-400">last:</span> {new Date(g.lastSeen).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span> : null}
                      </div>
                      {g.url ? <div className="text-[11px] text-secondary-500 mb-0.5 break-all"><span className="text-secondary-400">url:</span> {g.url}</div> : null}
                      {g.userAgent ? <div className="text-[11px] text-secondary-500 mb-2 break-all"><span className="text-secondary-400">ua:</span> {g.userAgent}</div> : null}
                      <div className="text-[11px] text-secondary-400 mb-1">Stack{g.source === 'frontend' ? ' (production stacks are minified — file/line map to the built bundle)' : ''}:</div>
                      <pre className="max-h-80 overflow-auto rounded-lg bg-white dark:bg-black border border-secondary-200 dark:border-secondary-800 p-3 text-[11px] leading-relaxed text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap break-words">{g.stack || 'No stack captured for this error.'}</pre>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] text-secondary-400">{rows.length} groups · scanned {scanned.toLocaleString()} events · click a row for the stack / file·line. Auto-collected from backend 5xx + frontend crashes (also in GCP Error Reporting).</p>
        </>
      )}
       </div>
       {(analysis || analyzing || analyzeErr) && (
         <aside className="mt-4 lg:mt-0 lg:w-96 lg:shrink-0">
           <div className="lg:sticky lg:top-4 rounded-xl border border-secondary-200 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-900/40 p-3">
             <div className="mb-2 flex items-center justify-between">
               <h3 className="text-sm font-semibold inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> AI triage</h3>
               <div className="flex items-center gap-2">
                 {analysis && <button onClick={copyAnalysis} className="text-[11px] text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200">{copied ? 'Copied' : 'Copy'}</button>}
                 <button onClick={() => { setAnalysis(null); setAnalyzeErr(''); }} className="text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200"><X className="w-3.5 h-3.5" /></button>
               </div>
             </div>
             {analyzing ? (
               <div className="py-6 flex items-center justify-center gap-2 text-xs text-secondary-500"><Loader2 className="w-4 h-4 animate-spin" /> Analysing {rows?.length || 0} error groups…</div>
             ) : analyzeErr ? (
               <p className="text-xs text-red-600">{analyzeErr}</p>
             ) : (
               <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-secondary-700 dark:text-secondary-200 font-mono">{analysis}</pre>
             )}
             <p className="mt-2 text-[10px] text-secondary-400">Prioritized fixes for the current window — copy into Claude Code.</p>
           </div>
         </aside>
       )}
      </div>
    </div>
  );
};

export default AdminCostPage;
